import * as React from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import MuiChip from "@mui/material/Chip"
import Container from "@mui/material/Container"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded"
import EdgesensorHighRoundedIcon from "@mui/icons-material/EdgesensorHighRounded"
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded"
import { EXTRA_FEATURE_ITEMS, FEATURES_SECTION, FEATURE_ITEMS } from "@/features/landing/content"

const icons = [<ViewQuiltRoundedIcon />, <EdgesensorHighRoundedIcon />, <DevicesRoundedIcon />]

interface ChipProps {
  selected?: boolean
}

const Chip = styled(MuiChip)<ChipProps>(({ theme }) => ({
  variants: [
    {
      props: ({ selected }) => !!selected,
      style: {
        background: "linear-gradient(to bottom right, hsl(210, 98%, 48%), hsl(210, 98%, 35%))",
        color: "hsl(0, 0%, 100%)",
        borderColor: (theme.vars || theme).palette.primary.light,
        "& .MuiChip-label": {
          color: "hsl(0, 0%, 100%)"
        },
        ...theme.applyStyles("dark", {
          borderColor: (theme.vars || theme).palette.primary.dark
        })
      }
    }
  ]
}))

interface MobileLayoutProps {
  selectedItemIndex: number
  handleItemClick: (index: number) => void
  selectedFeature: (typeof FEATURE_ITEMS)[0]
}

export function MobileLayout({ selectedItemIndex, handleItemClick, selectedFeature }: MobileLayoutProps) {
  if (!FEATURE_ITEMS[selectedItemIndex]) {
    return null
  }

  return (
    <Box
      sx={{
        display: { xs: "flex", sm: "none" },
        flexDirection: "column",
        gap: 2
      }}>
      <Box sx={{ display: "flex", gap: 2, overflow: "auto" }}>
        {FEATURE_ITEMS.map(({ title }, index) => (
          <Chip
            size="medium"
            key={index}
            label={title}
            onClick={() => handleItemClick(index)}
            selected={selectedItemIndex === index}
          />
        ))}
      </Box>
      <Card variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography gutterBottom sx={{ color: "text.primary", fontWeight: "medium" }}>
            {selectedFeature.title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            {selectedFeature.description}
          </Typography>
        </Box>
      </Card>
    </Box>
  )
}

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0)

  const handleItemClick = (index: number) => {
    setSelectedItemIndex(index)
  }

  const selectedFeature = FEATURE_ITEMS[selectedItemIndex]

  return (
    <Container id="features" sx={{ py: { xs: 8, sm: 16 } }}>
      <Box sx={{ width: { sm: "100%", md: "60%" } }}>
        <Typography component="h2" variant="h4" gutterBottom sx={{ color: "text.primary" }}>
          {FEATURES_SECTION.heading}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: { xs: 2, sm: 4 } }}>
          {FEATURES_SECTION.body}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row-reverse" },
          gap: 2
        }}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              flexDirection: "column",
              gap: 2,
              height: "100%"
            }}>
            {FEATURE_ITEMS.map(({ title, description }, index) => (
              <Box
                key={index}
                component={Button}
                onClick={() => handleItemClick(index)}
                sx={[
                  theme => ({
                    p: 2,
                    height: "100%",
                    width: "100%",
                    "&:hover": {
                      backgroundColor: (theme.vars || theme).palette.action.hover
                    }
                  }),
                  selectedItemIndex === index && {
                    backgroundColor: "action.selected"
                  }
                ]}>
                <Box
                  sx={[
                    {
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "left",
                      gap: 1,
                      textAlign: "left",
                      textTransform: "none",
                      color: "text.secondary"
                    },
                    selectedItemIndex === index && {
                      color: "text.primary"
                    }
                  ]}>
                  {icons[index]}

                  <Typography variant="h6">{title}</Typography>
                  <Typography variant="body2">{description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <MobileLayout
            selectedItemIndex={selectedItemIndex}
            handleItemClick={handleItemClick}
            selectedFeature={selectedFeature}
          />
        </Box>
      </Box>
      <Box sx={{ mt: { xs: 6, sm: 8 } }}>
        <Typography component="h3" variant="h5" gutterBottom sx={{ color: "text.primary" }}>
          More tools for sellers
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          These calculators help you estimate profit before you list or scale a product.
        </Typography>
        <Grid container spacing={2}>
          {EXTRA_FEATURE_ITEMS.map((feature, index) => (
            <Grid item xs={12} md={6} key={feature.title}>
              <Card variant="outlined" sx={{ height: "100%", p: 3 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={theme => ({
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor: (theme.vars || theme).palette.action.hover,
                        overflow: "hidden",
                        ...theme.applyStyles("dark", {
                          backgroundColor: (theme.vars || theme).palette.primary.dark
                        })
                      })}>
                      <Box
                        component="img"
                        src={feature.iconSrc}
                        alt={feature.iconAlt}
                        sx={{
                          width: 28,
                          height: 28,
                          objectFit: "contain",
                          display: "block"
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        New feature
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {feature.description}
                  </Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  )
}
